import { useMutation, useQuery, useInfiniteQuery, useQueryClient } from 'react-query';
import {
  getNotificationsByUser,
  getNotificationByUser,
  markAllNotificationsAsRead,
  getPendingNotifications,
  removePendingNotifications,
  type NotificationItem,
  type PendingNotification,
  getNotificationsSettings,
  NotificationSettingsResponse,
  UpdateNotificationsSettingsMethodBody,
  UpdateNotificationsSettingsModuleBody,
  UpdateBulkNotificationsSettingsBody,
  updateNotificationsSettingsModule,
  updateNotificationsSettingsMethod,
  updateBulkNotificationsSettings,
} from '@/services/notifications';
import { useNotificationContext } from '@/components/ui/notification-provider';

interface UseGetNotificationsParams {
  userId: string | null;
  page?: number;
  limit?: number;
  all: boolean;
  date: string;
  enabled?: boolean;
}

export const useGetNotifications = ({
  userId,
  page = 1,
  limit = 15,
  all = true,
  date = '',
  enabled = true,
}: UseGetNotificationsParams) => {
  const { isEnabled: notificationsEnabled } = useNotificationContext();
  
  return useQuery({
    queryKey: ['notifications', userId, page, limit, all, date],
    queryFn: () => getNotificationsByUser(userId as string, page, limit, all, date),
    enabled: notificationsEnabled && enabled && !!userId,
    keepPreviousData: true,
  });
};

export const useInfiniteNotifications = ({
  userId,
  limit = 50,
  all = true,
  date = '',
  enabled = true,
}: Omit<UseGetNotificationsParams, 'page'>) => {
  const { isEnabled: notificationsEnabled } = useNotificationContext();

  return useInfiniteQuery({
    queryKey: ['infinite-notifications', userId, limit, all, date],
    queryFn: ({ pageParam = 1 }) => getNotificationsByUser(userId as string, pageParam, limit, all, date),
    enabled: notificationsEnabled && enabled && !!userId,
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.meta?.currentPage || 0;
      const totalPages = lastPage.meta?.totalPages || 0;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    keepPreviousData: true,
  });
};

export const notificationKey = (userId: string, id: string) => ['notification', userId, id] as const;

export const useGetNotification = (
  userId: string | null,
  notificationId: string | null,
  enabled = true,
) => {
  const { isEnabled: notificationsEnabled } = useNotificationContext();
  const queryClient = useQueryClient();
  const query = useQuery<NotificationItem>({
    queryKey: ['notification', userId, notificationId],
    queryFn: () => getNotificationByUser(userId as string, notificationId as string),
    enabled: notificationsEnabled && enabled && !!userId && !!notificationId,
    keepPreviousData: false,
  });

  const invalidate = () => {
    if (!userId || !notificationId) return;
    queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
  };

  return { ...query, invalidate };
};

export const useMarkAllNotificationsAsRead = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void,
) => {
  const { isEnabled: notificationsEnabled } = useNotificationContext();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userId: string) => markAllNotificationsAsRead(userId),
    onSuccess: (_, userId) => {
      if (notificationsEnabled) {
        queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
      }
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};


export const useGetPendingNotifications = (
  userId: string | null,
  page = 1,
  limit = 5,
  enabled = true,
) => {
  const { isEnabled: notificationsEnabled } = useNotificationContext();
  
  return useQuery<PendingNotification>({
    queryKey: ['pending-notifications', userId, page, limit],
    queryFn: () => getPendingNotifications(userId as string, page, limit),
    enabled: notificationsEnabled && enabled && !!userId,
    refetchOnMount: true,
  });
};

export const useRemovePendingNotifications = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void,
) => {
  const { isEnabled: notificationsEnabled } = useNotificationContext();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userId: string) => removePendingNotifications(userId),
    onSuccess: (_, userId) => {
      if (notificationsEnabled) {
        queryClient.invalidateQueries({ queryKey: ['pending-notifications', userId] });
      }
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetNotificationsSettings = (userId: string | null, enabled = true) => {
  return useQuery({
    queryKey: ['notifications-settings', userId],
    queryFn: () => getNotificationsSettings(userId as string),
    enabled: enabled && !!userId,
    keepPreviousData: false,
  });
};

export const useUpdateNotificationsSettingsModule = (
  userId: string,
  onSuccess?: (data: NotificationSettingsResponse) => void,
  onError?: (error: unknown) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateNotificationsSettingsModuleBody) =>
      updateNotificationsSettingsModule(userId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['notifications-settings', userId],
      });
      onSuccess?.(data);
    },
    onError: (error: unknown) => {
      onError?.(error);
    },
  });
};

export const useUpdateNotificationsSettingsMethod = (
  userId: string,
  onSuccess?: (data: NotificationSettingsResponse) => void,
  onError?: (error: unknown) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateNotificationsSettingsMethodBody) =>
      updateNotificationsSettingsMethod(userId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['notifications-settings', userId],
      });
      onSuccess?.(data);
    },
    onError: (error: unknown) => {
      onError?.(error);
    },
  });
};

export const useUpdateBulkNotificationsSettings = (
  userId: string,
  onSuccess?: (data: NotificationSettingsResponse) => void,
  onError?: (error: unknown) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateBulkNotificationsSettingsBody) =>
      updateBulkNotificationsSettings(userId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['notifications-settings', userId],
      });
      onSuccess?.(data);
    },
    onError: (error: unknown) => {
      onError?.(error);
    },
  });
};
